// AdminWsListener.jsx
import { useEffect, useRef } from "react";
import { notification } from "antd";
import { API_BASE_URL, WS_ENDPOINT_URL } from "configs/AppConfig";
import { ensureWS, subscribe, isConnected } from "./wsClient";
import { useAdminNotifications } from "contexts/AdminNotificationsContext";
import { playNotificationSound } from "utils/notificationSound";

export default function AdminWsListener() {
  const { addFromWs } = useAdminNotifications();
  const tokenPollRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    function getToken() {
      const raw = localStorage.getItem("auth_token");
      if (!raw) return null;
      return raw.replace(/^Bearer\s+/i, "").replace(/^"|"$/g, "");
    }

    function getWsUrl() {
      const base = (WS_ENDPOINT_URL || API_BASE_URL || "").replace(/\/api\/?$/, "");
      return WS_ENDPOINT_URL
        ? WS_ENDPOINT_URL
        : base.replace(/^http/, "ws") + "/ws/";
    }

    // Show toast notification in the app
    function showToast(title, body, type = 'info') {
      const notifType = type === 'booking' ? 'success' : 
                        type === 'gift_card' ? 'info' : 'info';
      
      notification[notifType]({
        message: title,
        description: body,
        duration: 6,
        placement: 'topRight',
      });
    }

    function handleMessage(ev) {
      // ev.data is a JSON string from server
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }

      // Ignore pong messages (keepalive)
      if (msg?.type === "pong") return;
      
      // Also handle auth confirmation
      if (msg?.type === "auth" && msg?.ok) {
        console.log("[ws] authenticated as", msg?.user?.role);
        return;
      }

      const { event, payload } = msg || {};
      if (!event || !payload) return;

      console.log("[ws] received event:", event, payload);

      // Handle booking events
      if (event === "booking.created" || event === "booking.updated") {
        playNotificationSound();
        
        // ✅ Push into notification list store
        addFromWs({ ...payload, event, type: 'booking' });
        
        // ✅ Show in-app toast notification
        showToast(payload.title || "New Booking", payload.body || "", 'booking');

        // ✅ Browser notification (requires permission)
        if (Notification?.permission === "granted") {
          new Notification(payload.title || "NextGen VR", {
            body: payload.body || "",
            icon: "/favicon.ico",
          });
        }
      }
      
      // Handle notification events (gift cards, etc.)
      if (event === "notification") {
        playNotificationSound();
        
        // payload contains: id, type, event as notifEvent, title, body, url, source, createdAt
        addFromWs({ 
          _id: payload.id || payload._id, 
          title: payload.title,
          body: payload.body,
          url: payload.url,
          source: payload.source,
          createdAt: payload.createdAt,
          event: payload.event, // the inner event like gift_card.purchased
          type: payload.type || 'gift_card',
        });

        // ✅ Show in-app toast notification
        showToast(payload.title || "Notification", payload.body || "", payload.type || 'gift_card');

        // ✅ Browser notification (requires permission)
        if (Notification?.permission === "granted") {
          new Notification(payload.title || "NextGen VR", {
            body: payload.body || "",
            icon: "/favicon.ico",
          });
        }
      }
    }

    function start() {
      const token = getToken();
      if (!token) return false;

      const wsUrl = getWsUrl();
      console.log("[ws] starting singleton on", wsUrl);

      ensureWS(wsUrl, token);

      const unsub = subscribe(handleMessage);
      return unsub;
    }

    // Try start immediately
    let cleanup = start();
    cleanupRef.current = cleanup || null;

    // If token not ready yet, poll for it for a bit
    if (!cleanup) {
      tokenPollRef.current = setInterval(() => {
        if (cleanupRef.current) return;

        const maybeCleanup = start();
        if (maybeCleanup) {
          cleanupRef.current = maybeCleanup;
          clearInterval(tokenPollRef.current);
          tokenPollRef.current = null;
        }
      }, 1000);
    }

    return () => {
      if (tokenPollRef.current) {
        clearInterval(tokenPollRef.current);
        tokenPollRef.current = null;
      }
      if (typeof cleanupRef.current === "function") {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [addFromWs]);

  return null;
}
